import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ExternalLink } from 'lucide-react'
import { MSection, SectionHeader } from '@/components/marketing/section'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'

const TITLE = 'n8n Integration for Uptime Monitoring — Uptrack'
const DESCRIPTION = 'Manage Uptrack monitors and incidents from n8n workflows, and start a workflow when a monitor goes down or recovers. Open source community node.'

export const Route = createFileRoute('/_site/integrations/n8n')({
  component: N8nIntegrationPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: TITLE },
      { property: 'og:description', content: DESCRIPTION },
      { property: 'og:url', content: 'https://uptrack.app/integrations/n8n' },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://uptrack.app/og/home.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/integrations/n8n' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'Integrations', item: 'https://uptrack.app/integrations' },
            { '@type': 'ListItem', position: 3, name: 'n8n', item: 'https://uptrack.app/integrations/n8n' },
          ],
        }),
      },
    ],
  }),
})

const NPM_URL = 'https://www.npmjs.com/package/n8n-nodes-uptrack'
const GITHUB_URL = 'https://github.com/Uptrack-App/n8n-nodes-uptrack'

const OPERATIONS = [
  { resource: 'Monitor', ops: 'Get many (with search), get, create (HTTP or heartbeat), update, pause, resume, delete' },
  { resource: 'Incident', ops: 'Get many (only ongoing, or all), get with its updates, acknowledge, post an update' },
  { resource: 'Uptrack Trigger', ops: 'Starts a workflow when a monitor goes down, recovers, or a watched third-party status page cannot be read' },
]

const STEPS = [
  {
    step: '1',
    title: 'Install the node',
    desc: 'On self-hosted n8n, open Settings, then Community Nodes, then Install, and enter n8n-nodes-uptrack.',
  },
  {
    step: '2',
    title: 'Add your API key',
    desc: 'Create an API key in the Uptrack dashboard. In n8n, create an Uptrack API credential and paste it. Select Test to check it.',
  },
  {
    step: '3',
    title: 'Build the workflow',
    desc: 'Add the Uptrack node to read or change monitors and incidents, or the Uptrack Trigger node to start from an alert.',
  },
]

const IDEAS = [
  'Open a Linear or Jira ticket when a monitor goes down, and add a note when it recovers.',
  'Post the incident to a team channel with the monitor name and the failure details from the alert.',
  'Pause the monitors of a service while a deploy runs, then resume them.',
  'Add a row to a spreadsheet for each incident, with its start and end time.',
]

function N8nIntegrationPage() {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              Uptime monitoring in your n8n workflows.
            </h1>
            <p className="mt-5 max-w-2xl text-lead text-body">
              The Uptrack community node reads and manages your monitors and incidents from n8n, and a trigger starts a workflow the moment a monitor goes down or recovers. It is open source (MIT).
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <a href={NPM_URL} target="_blank" rel="noopener noreferrer">
                  Get the node on npm
                  <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="h-11 px-5 text-[15px]" asChild>
                <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
                  Source on GitHub
                  <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
            </div>
            <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
              The node is not verified by n8n yet, so n8n Cloud cannot install it yet. It installs on self-hosted n8n today.
            </p>
          </div>
        </div>
      </section>

      <MSection tone="raised">
        <SectionHeader title="What the node does" lead="Two nodes and two credentials. Operations call the Uptrack API with your API key." />
        <dl className="mt-8 grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-3">
          {OPERATIONS.map((item) => (
            <div key={item.resource} className="border-t border-border pt-4">
              <dt className="text-[17px] font-semibold text-foreground">{item.resource}</dt>
              <dd className="mt-2 text-[15px] leading-relaxed text-body">{item.ops}</dd>
            </div>
          ))}
        </dl>
      </MSection>

      <MSection>
        <SectionHeader title="How to set it up" />
        <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {STEPS.map((item) => (
            <li key={item.step} className="rounded-lg border border-border bg-card p-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background num text-sm font-semibold text-foreground">
                {item.step}
              </span>
              <h3 className="mt-4 text-[17px] font-semibold text-foreground">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.desc}</p>
            </li>
          ))}
        </ol>
      </MSection>

      <MSection tone="raised">
        <SectionHeader
          title="Start a workflow from an alert"
          lead="The Uptrack Trigger node receives the signed webhook that Uptrack sends when a monitor changes state."
        />
        <ol className="mt-8 max-w-3xl list-decimal space-y-3 pl-5 text-[15px] leading-relaxed text-body">
          <li>Add the Uptrack Trigger node to a workflow and copy its webhook URL.</li>
          <li>In Uptrack, create an alert channel of type Webhook, paste the URL, and add the channel to the monitors you want.</li>
          <li>Copy the signing secret that Uptrack shows once, and save it in the Uptrack Webhook API credential in n8n.</li>
        </ol>
        <p className="mt-5 max-w-3xl text-[15px] leading-relaxed text-body">
          The trigger checks the signature of every request and refuses old or unsigned ones. Webhook alert channels need a paid plan, so the trigger does not work on the free plan. The payload is described in{' '}
          <Link to="/guides/webhook-payloads" className="underline">
            webhook payloads
          </Link>
          .
        </p>
      </MSection>

      <MSection>
        <SectionHeader title="Workflow ideas" lead="Built from the node and n8n's own nodes." />
        <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
          {IDEAS.map((item) => (
            <li key={item} className="flex items-start gap-3 border-t border-border pt-3 text-[15px] text-body">
              <span aria-hidden="true" className="mt-0.5 font-mono text-status-up">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </MSection>

      <MSection tone="raised">
        <SectionHeader title="Limits" />
        <ul className="mt-8 max-w-3xl list-disc space-y-3 pl-5 text-[15px] leading-relaxed text-body">
          <li>This first version creates HTTP and heartbeat monitors. Other monitor types are made in the dashboard and can still be read and updated.</li>
          <li>Your plan sets the number of monitors and the shortest check interval. Uptrack answers with an error when you go over, and the node shows the message.</li>
          <li>You add the webhook channel in Uptrack by hand. The node does not register it for you.</li>
        </ul>
        <p className="mt-5 max-w-3xl text-[15px] leading-relaxed text-body">
          Prefer plain HTTP? Everything the node does uses the public API, which has an{' '}
          <a href="https://api.uptrack.app/openapi.json" className="underline" target="_blank" rel="noopener noreferrer">
            OpenAPI description
          </a>
          , so the HTTP Request node works too. Using an AI assistant instead? See{' '}
          <Link to="/ai" className="underline">
            AI and MCP
          </Link>
          .
        </p>
      </MSection>

      <section>
        <div className="container mx-auto px-4 py-12 md:py-[72px]">
          <div className="grid grid-cols-1 items-center gap-8 rounded-frame border border-border bg-card p-8 shadow-raised md:p-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Connect your monitors to your workflows</h2>
              <p className="mt-3 max-w-xl text-lead text-body">
                Know in seconds when a site is down, then let n8n do the rest.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:col-span-4 lg:justify-end">
              <StartFreeButton />
              <Button size="lg" variant="outline" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">See plans</Link>
              </Button>
              <PlanFinePrint className="w-full lg:text-right" />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
